/**
 * Central API configuration for NexaWave
 * Handles switching between local emulator and live production URLs
 */

const LOCAL_EMULATOR_URL = 'http://127.0.0.1:5001/nexawave-4d18a/us-central1';

// In production, we use the Firebase Hosting URL which routes to our functions via /api/
// This avoids CORS issues and keeps URLs clean.
const PROD_URL = "https://nexawave-4d18a.web.app/api";

// Mapping function names to their rewritten paths
const FUNCTION_PATH_MAP = {
  'getDeliveryDate': '/delivery-date',
  'createOrder': '/create-order',
  'createRazorpayOrder': '/create-razorpay-order',
  'verifyRazorpayPayment': '/verify-payment'
};

export const getApiBaseUrl = (functionName = '') => {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  if (isLocal) {
    return `${LOCAL_EMULATOR_URL}/${functionName}`;
  }
  
  const path = FUNCTION_PATH_MAP[functionName] || `/${functionName}`;
  return `${PROD_URL}${path}`;
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Helper to handle fetch errors and log detailed information
 */
export const safeFetch = async (url, options = {}) => {
  try {
    const response = await fetch(url, options);
    
    // If we got a 404 on the production URL, it might mean the functions aren't deployed
    if (!response.ok && response.status === 404 && url.includes('cloudfunctions.net')) {
      console.warn(`API 404 at ${url}. If this is production, ensure your functions are deployed.`);
    }
    
    return response;
  } catch (error) {
    // If the local emulator is not running (CONNECTION_REFUSED), try falling back to production
    if (url.includes('127.0.0.1:5001') || url.includes('localhost:5001')) {
      console.warn("Local emulator not detected. Falling back to production API...");
      
      const localBase = 'http://127.0.0.1:5001/nexawave-4d18a/us-central1';
      
      if (url.startsWith(localBase)) {
        // Extract function name
        const functionName = url.replace(`${localBase}/`, '').split('?')[0];
        const path = FUNCTION_PATH_MAP[functionName] || `/${functionName}`;
        const prodUrl = `${PROD_URL}${path}${url.includes('?') ? '?' + url.split('?')[1] : ''}`;

        try {
          const prodResponse = await fetch(prodUrl, options);
          if (prodResponse.ok) {
            console.log("Successfully connected to production API fallback.");
          }
          return prodResponse;
        } catch (prodError) {
          console.error("Production fallback also failed:", prodError);
          
          // MOCK FALLBACK for getDeliveryDate (so UI doesn't break for developer)
          if (url.includes('getDeliveryDate')) {
            console.log("Using Mock Delivery Date fallback for development UI.");
            return new Response(JSON.stringify({
              success: true,
              estimated_delivery_date: new Date(Date.now() + 3*24*60*60*1000).toISOString().split('T')[0],
              is_mock: true
            }), { status: 200, headers: { 'Content-Type': 'application/json' } });
          }
        }
      }
    }

    console.error(`Fetch failure at ${url}:`, error.message);
    console.warn("This often means the backend server is not running or CORS is blocking the request.");
    throw error;
  }
};
